<template>
    <div>
        <RegistrationRequestFilters
            v-model="filters"
            :has-active-filters="hasActiveFilters"
            @submit="loadData()"
            @reset="resetFilters"
        />
        <v-data-table-server
            v-model="selected"
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            :headers="useHeaders"
            :items="items"
            :items-per-page-options="[50, -1]"
            :loading="isLoading"
            :items-length="registrationRequestsCount"
            disable-sort
            density="compact"
            color="primary"
            class="elevation-1 table-server-selected"
            data-cy="registration-request-table"
            show-select
            return-object
            @update:options="loadData"
        >
            <template v-slot:item.name="{item}">
                <nuxt-link
                    :to="{path: '/registration-requests/edit', state: {companyInfoMessage: item.companyInfoMessage}}"
                    class="text-link"
                >
                    {{ item.name }}
                </nuxt-link>
            </template>
            <template v-slot:item.active="{item}">
                {{ item.active ? $t('active') : $t('inactive') }}
            </template>
            <template v-slot:item.delete="{item}">
                <v-btn
                    variant="text"
                    color="error"
                    class="text-caption"
                    data-cy="delete"
                    density="compact"
                    @click="deleteRequest(item.id)"
                >
                    {{ $t('delete') }}
                </v-btn>
            </template>
            <template v-slot:item.activate="{item}">
                <v-btn
                    variant="text"
                    color="primary"
                    class="text-caption"
                    data-cy="activate"
                    density="compact"
                    @click="activateRequest(item.id)"
                >
                    {{ $t('activate') }}
                </v-btn>
            </template>
        </v-data-table-server>
        <ConfirmationModal
            v-model="isShowModal"
            :text="isShowDeleteRequestModal ? $t('delete_request_text') : $t('activate_request_text')"
            :confirmation-btn-text="isShowDeleteRequestModal ? $t('confirm_delete') : $t('confirm')"
            :close-btn-text="$t('cancel')"
            @close="closeModal"
            @confirm="changeRegistrationRequest"
        />
    </div>
</template>

<script setup lang="ts">
    import type {DepartmentFiltersType} from '~/utils/types/account/departments/types'
    import RegistrationRequestFilters from '~/components/account/registrationRequests/RegistrationRequestFilters.vue'
    import type {RegistrationRequestFiltersType} from '~/utils/types/account/registrationRequests/types'
    import ConfirmationModal from '~/components/modals/ConfirmationModal.vue'

    const initRegistrationRequestFilters = (): RegistrationRequestFiltersType => ({
        countryId: null,
        cityId: null,
        active: true,
        type: null,
        name: null,
    })

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const isLoading = ref<boolean>(false)
    const filters = ref<DepartmentFiltersType>(initRegistrationRequestFilters())
    const registrationRequests = ref<any[]>([])
    const registrationRequestsCount = ref<number>(0)
    const isShowDeleteRequestModal = ref<boolean>(false)
    const isShowActivateRequestModal = ref<boolean>(false)
    const isShowModal = ref<boolean>(false)
    const chosenCompanyId = ref<number>(null)

    const selected = defineModel<any[]>()
    const itemsPerPage = ref(50)
    const page = ref(1)

    const app = useNuxtApp()
    const {t} = useI18n()

    async function loadData(): Promise<void> {
        const offset = (page.value - 1) * itemsPerPage.value
        const limit = itemsPerPage.value
        try {
            isLoading.value = true
            const rq = {
                ...filters.value,
                offset,
                limit,
            }
            if (!rq.type) delete rq.type
            const {companies, companiesCount} = await app.$api.companyRegistration.get(rq)
            registrationRequests.value = companies
            registrationRequestsCount.value = companiesCount
        } catch {
            registrationRequests.value = []
            registrationRequestsCount.value = 0
        } finally {
            isLoading.value = false
        }
    }

    defineExpose({loadData})

    const useHeaders: globalThis.ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('type'), value: 'type'},
            {title: t('country'), value: 'countryName'},
            {title: t('city'), value: 'cityName'},
            {title: t('phone'), value: 'phones', minWidth: '160px'},
            {title: t('email'), value: 'emails'},
            {title: t('registration'), value: 'registrationDate'},
            {title: t('last_contact'), value: 'contactDate'},
            {title: t('assigned_to'), value: 'assigned'},
            {title: t('stage'), value: 'stage'},
            {title: t('rating'), value: 'rating'},
            {title: t('status'), value: 'active'},
            {title: '', value: 'delete'},
            {title: '', value: 'activate'},
        ]
        return headers
    })

    const items = computed(() => {
        return registrationRequests.value.map(
            ({id, companyId, name, type, countryName, cityName, phones, emails, registrationDate, active}, index) => ({
                id,
                companyId,
                name,
                type: t(`registrationType.${type}`),
                countryName,
                cityName,
                phones: phones.join('\n'),
                emails: emails.join('\n'),
                registrationDate: registrationDate ? app.$filters.dateTimeFormat(registrationDate) : '',
                contactDate: registrationDate ? app.$filters.dateTimeFormat(registrationDate) : '',
                assigned: '',
                stage: t('cold'),
                rating: 1,
                active,
                companyInfoMessage: clone(registrationRequests.value[index]),
            })
        )
    })

    const resetFilters = (): void => {
        filters.value = initRegistrationRequestFilters()
        page.value = 1
        loadData()
    }

    const hasActiveFilters = computed(() => {
        return !isEqual(initRegistrationRequestFilters(), filters.value)
    })

    const activateRequest = id => {
        chosenCompanyId.value = id
        isShowDeleteRequestModal.value = false
        isShowActivateRequestModal.value = true
        isShowModal.value = true
    }

    const deleteRequest = id => {
        chosenCompanyId.value = id
        isShowDeleteRequestModal.value = true
        isShowActivateRequestModal.value = false
        isShowModal.value = true
    }

    const closeModal = (): void => {
        isShowModal.value = false
        chosenCompanyId.value = null
    }

    const changeRegistrationRequest = async (): Promise<void> => {
        isShowModal.value = false
        if (isShowDeleteRequestModal.value) {
            try {
                isLoading.value = true
                await app.$api.companyRegistrationRequestDelete.post(chosenCompanyId.value)
                page.value = 1
                await loadData()
                app.$toast.success(t('messages.success.company_delete'))
            } catch (e) {
                app.$toast.error(t('error_message.save_error'))
            } finally {
                isLoading.value = false
            }
        }

        if (isShowActivateRequestModal.value) {
            try {
                isLoading.value = true
                await app.$api.companyRegistrationRequestActivate.post(chosenCompanyId.value)
                page.value = 1
                await loadData()
                app.$toast.success(t('messages.success.company_activate'))
            } catch (e) {
                app.$toast.error(t('error_message.save_error'))
            } finally {
                isLoading.value = false
            }
        }
    }
</script>
